import { PresenceColorProvider } from '@fluidx/office-fluid-types';
import { Deferred } from '../deferred/Deferred';

/**
 * Persona color palette from the redlines
 * https://microsoft.sharepoint-df.com/:p:/r/teams/OfficeOnlinePrague/_layouts/15/guestaccess.aspx?e=XfGYeG&share=EZH2ZfMFYxtFtSTGgVhQ6A4B5aR1jvHdHsPhZKulena3Ww&PreviousSessionID=0c90b23a-84da-de79-a705-cbebb7965890 - Slide 4
 */
const colorPalette: string[] = [
  '#C239B3',
  '#CA5010',
  '#E3008C',
  '#A4262C',
  '#498205',
  '#69797E',
  '#881798',
  '#D13438',
  '#0078D4',
  '#038387',
  '#750B1C',
  '#986F0B',
  '#4F6BED',
  '#8764B8',
  '#0B6A0B',
  '#7A7574',
  '#5C2E91',
  '#005B70',
  '#8E562E'
];

/**
 * Default implementation of presence color provider.
 */
export class DefaultPresenceColorProvider implements PresenceColorProvider {
  public get PresenceColorProvider() {
    return this;
  }

  /**
   * This is a basic implementation of presence color assignment at the application level. It
   * assigns the colors in order and then starts from the beginning of the list. It does
   * not track when users leave the session, so a more clever implementation would reassign colors
   * that were assigned to users that have left in order to reduce collisions.
   */
  private nextColorToAssignIndex = 0;

  // Track the users whose color assignment are in flight.
  // Used to avoid assigning multiple colors to the same user.
  private colorAssignmentInFlight: Set<string> = new Set();
  private deferredColorAssignmentPromise: { [userId: string]: Deferred<string>[] } = {};

  // Map from userIds to the color already assigned to them. Used to make sure we don't assign multiple colors for the same user.
  private assignedColors = new Map<string, string>();

  async getPresenceColorForUser(userId: string): Promise<string> {
    const cachedValue = this.assignedColors.get(userId);
    if (cachedValue) {
      return Promise.resolve(cachedValue);
    }

    // If assigning color to this user is in progress, return a promise that will be resolved when color is assigned.
    if (this.colorAssignmentInFlight.has(userId)) {
      const onColorAssignedPromise = new Deferred<string>();
      if (this.deferredColorAssignmentPromise[userId] === undefined) {
        this.deferredColorAssignmentPromise[userId] = [];
      }
      this.deferredColorAssignmentPromise[userId].push(onColorAssignedPromise);
      return onColorAssignedPromise;
    }

    this.colorAssignmentInFlight.add(userId);

    const assignedColor = colorPalette[this.nextColorToAssignIndex];

    this.nextColorToAssignIndex = (this.nextColorToAssignIndex + 1) % colorPalette.length;

    this.assignedColors.set(userId, assignedColor);

    this.colorAssignmentInFlight.delete(userId);
    // If there are promises waiting for this color assignment, resolve them.
    if (this.deferredColorAssignmentPromise[userId] !== undefined) {
      for (const promise of this.deferredColorAssignmentPromise[userId]) {
        promise.resolve(assignedColor);
      }
    }
    delete this.deferredColorAssignmentPromise[userId];

    return Promise.resolve(assignedColor);
  }
}
